import React, {
  createContext,
  useCallback,
  useContext,
  useId,
  useMemo,
  useRef,
  useSyncExternalStore,
} from "react"

import type { AppCommand } from "@/lib/appCommands"

function areStringArraysEqual(left?: string[], right?: string[]) {
  if (!left && !right) {
    return true
  }

  if (!left || !right || left.length !== right.length) {
    return false
  }

  return left.every((value, index) => value === right[index])
}

function areCommandsEqual(left: AppCommand[], right: AppCommand[]) {
  if (left.length !== right.length) {
    return false
  }

  return left.every((leftCommand, index) => {
    const rightCommand = right[index]

    return (
      leftCommand.action === rightCommand?.action &&
      leftCommand.available === rightCommand?.available &&
      leftCommand.description === rightCommand?.description &&
      leftCommand.group === rightCommand?.group &&
      leftCommand.id === rightCommand?.id &&
      leftCommand.isBranch === rightCommand?.isBranch &&
      leftCommand.key === rightCommand?.key &&
      leftCommand.kind === rightCommand?.kind &&
      leftCommand.label === rightCommand?.label &&
      leftCommand.parentId === rightCommand?.parentId &&
      leftCommand.scope === rightCommand?.scope &&
      leftCommand.searchOnly === rightCommand?.searchOnly &&
      areStringArraysEqual(leftCommand.aliases, rightCommand?.aliases) &&
      areStringArraysEqual(leftCommand.keywords, rightCommand?.keywords)
    )
  })
}

interface CommandRegistryControllerContextValue {
  clearCommands: (namespace: string) => void
  setCommands: (namespace: string, commands: AppCommand[]) => void
}

interface CommandRegistryStore {
  getSnapshot: () => AppCommand[]
  subscribe: (listener: () => void) => () => void
}

const CommandRegistryControllerContext =
  createContext<CommandRegistryControllerContextValue | null>(null)
const CommandRegistryStoreContext = createContext<CommandRegistryStore | null>(
  null,
)

export function CommandRegistryProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const commandRegistrationsRef = useRef<Map<string, AppCommand[]>>(new Map())
  const snapshotRef = useRef<AppCommand[]>([])
  const listenersRef = useRef(new Set<() => void>())

  const notify = useCallback(() => {
    listenersRef.current.forEach((listener) => {
      listener()
    })
  }, [])

  const setCommands = useCallback(
    (namespace: string, commands: AppCommand[]) => {
      const currentRegistrations = commandRegistrationsRef.current
      const existingCommands = currentRegistrations.get(namespace)
      if (existingCommands && areCommandsEqual(existingCommands, commands)) {
        return
      }

      const nextRegistrations = new Map(currentRegistrations)
      nextRegistrations.set(namespace, commands)
      commandRegistrationsRef.current = nextRegistrations
      snapshotRef.current = Array.from(nextRegistrations.values()).flat()
      notify()
    },
    [notify],
  )

  const clearCommands = useCallback(
    (namespace: string) => {
      const currentRegistrations = commandRegistrationsRef.current
      if (!currentRegistrations.has(namespace)) {
        return
      }

      const nextRegistrations = new Map(currentRegistrations)
      nextRegistrations.delete(namespace)
      commandRegistrationsRef.current = nextRegistrations
      snapshotRef.current = Array.from(nextRegistrations.values()).flat()
      notify()
    },
    [notify],
  )

  const controllerValue = useMemo(
    () => ({
      clearCommands,
      setCommands,
    }),
    [clearCommands, setCommands],
  )

  const storeValue = useMemo<CommandRegistryStore>(
    () => ({
      getSnapshot: () => snapshotRef.current,
      subscribe: (listener: () => void) => {
        listenersRef.current.add(listener)

        return () => {
          listenersRef.current.delete(listener)
        }
      },
    }),
    [],
  )

  return (
    <CommandRegistryStoreContext.Provider value={storeValue}>
      <CommandRegistryControllerContext.Provider value={controllerValue}>
        {children}
      </CommandRegistryControllerContext.Provider>
    </CommandRegistryStoreContext.Provider>
  )
}

export function useCommandRegistry() {
  const store = useContext(CommandRegistryStoreContext)

  if (!store) {
    throw new Error(
      "useCommandRegistry must be used within a CommandRegistryProvider",
    )
  }

  const registeredCommands = useSyncExternalStore(
    store.subscribe,
    store.getSnapshot,
    store.getSnapshot,
  )

  return {
    registeredCommands,
  }
}

function useCommandRegistryController() {
  const context = useContext(CommandRegistryControllerContext)

  if (!context) {
    throw new Error(
      "useCommandRegistryController must be used within a CommandRegistryProvider",
    )
  }

  return context
}

export function useRegisterCommands(commands: AppCommand[], enabled = true) {
  const namespace = useId()
  const { clearCommands, setCommands } = useCommandRegistryController()
  const hasRegisteredRef = useRef(false)

  React.useEffect(() => {
    if (!enabled) {
      return
    }

    setCommands(namespace, commands)
    hasRegisteredRef.current = true
  }, [commands, enabled, namespace, setCommands])

  React.useEffect(() => {
    if (enabled || !hasRegisteredRef.current) {
      return
    }

    clearCommands(namespace)
    hasRegisteredRef.current = false
  }, [clearCommands, enabled, namespace])

  React.useEffect(() => {
    return () => {
      if (hasRegisteredRef.current) {
        clearCommands(namespace)
        hasRegisteredRef.current = false
      }
    }
  }, [clearCommands, namespace])
}
